import { useEffect, useMemo, useState } from 'react';
import { getCompanies } from '../api/companies';
import { Topbar } from '../layout/Topbar';
import type { Company } from '../types';

type AgentSection = 'overview' | 'journey' | 'tools' | 'production' | 'versions';

const agentSections: Array<{ id: AgentSection; label: string }> = [
  { id: 'overview', label: 'Visão geral' },
  { id: 'journey', label: 'Jornada comercial' },
  { id: 'tools', label: 'Ferramentas' },
  { id: 'production', label: 'Produção' },
  { id: 'versions', label: 'Versões' },
];

const journey = [
  ['01', 'Recepcionar e entender a intenção', 'Apresenta Alyce no primeiro contato e identifica se o assunto é comercial.'],
  ['02', 'Localizar o endereço', 'Coleta cidade, bairro, rua, número e complemento sem repetir informações.'],
  ['03', 'Validar cobertura', 'Aguarda a consulta Geogrid. Sem integração, nunca confirma cobertura ou prazo.'],
  ['04', 'Qualificar a necessidade', 'Entende perfil de uso, pessoas, dispositivos, urgência e responsável pela contratação.'],
  ['05', 'Recomendar e confirmar o plano', 'Apresenta 100, 300 ou 600 Mega somente com valores comerciais vigentes.'],
  ['06', 'Preparar a proposta', 'Confirma titular, telefone, CPF/CNPJ, e-mail e endereço antes de encaminhar.'],
  ['07', 'Encaminhar com segurança', 'Cria solicitação interna e transfere ao comercial quando houver ação humana necessária.'],
];

const tools = [
  { name: 'Catálogo comercial', detail: '100, 300 e 600 Mega', state: 'Pronto para configurar', tone: 'primary' },
  { name: 'Cobertura Geogrid', detail: 'CTO, distância e porta disponível', state: 'Integração pendente', tone: 'warning' },
  { name: 'Consulta SGP', detail: 'Evita CPF/CNPJ duplicado', state: 'Supervisionada', tone: 'success' },
  { name: 'Proposta de instalação', detail: 'Ficha preenchida pelo atendimento', state: 'Em implementação', tone: 'neutral' },
  { name: 'Fila comercial', detail: 'Transferência com resumo e contexto', state: 'Disponível', tone: 'success' },
];

function AgentEmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="agents-empty-state">
      <span className="agents-empty-mark" aria-hidden="true">□</span>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
}

export function AgentsPage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [activeSection, setActiveSection] = useState<AgentSection>('overview');

  useEffect(() => {
    getCompanies()
      .then((items) => {
        setCompanies(items);
        setCompanyId((current) => current || items[0]?.id || '');
      })
      .catch(() => setCompanies([]));
  }, []);

  const selectedCompany = useMemo(
    () => companies.find((company) => company.id === companyId),
    [companies, companyId],
  );

  const renderPanel = () => {
    if (activeSection === 'journey') {
      return (
        <section className="agents-content-panel" aria-labelledby="agent-journey-title">
          <div className="agents-panel-heading">
            <div>
              <p className="agents-eyebrow">Fluxo estruturado</p>
              <h2 id="agent-journey-title">Jornada comercial da Alyce</h2>
            </div>
            <span className="agents-draft-chip">Contrato operacional</span>
          </div>
          <p className="agents-panel-intro">Este é o contrato de atendimento comercial da Alyce para a Evoluir Telecom. Alterações passam por validação e publicação antes de responderem no WhatsApp.</p>
          <ol className="agents-journey-list">
            {journey.map(([number, title, description]) => (
              <li className="agents-journey-step" key={number}>
                <span className="agents-step-number">{number}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      );
    }

    if (activeSection === 'tools') {
      return (
        <section className="agents-content-panel" aria-labelledby="agent-tools-title">
          <div className="agents-panel-heading">
            <div>
              <p className="agents-eyebrow">Ações seguras</p>
              <h2 id="agent-tools-title">Ferramentas do agente</h2>
            </div>
          </div>
          <p className="agents-panel-intro">Uma ferramenta só poderá executar uma ação quando houver dados suficientes e permissão explícita da empresa.</p>
          <div className="agents-tools-grid">
            {tools.map((tool) => (
              <article className="agents-tool-card" key={tool.name}>
                <span className={`agents-tool-status is-${tool.tone}`}>{tool.state}</span>
                <h3>{tool.name}</h3>
                <p>{tool.detail}</p>
              </article>
            ))}
          </div>
          <div className="agents-geogrid-notice" role="note">
            <strong>Proteção ativa:</strong> até o GeoGrid estar integrado, cobertura, CTO, porta e instalação permanecem como “a confirmar”.
          </div>
        </section>
      );
    }

    if (activeSection === 'production') {
      return (
        <section className="agents-content-panel" aria-labelledby="agent-production-title">
          <div className="agents-panel-heading">
            <div>
              <p className="agents-eyebrow">WhatsApp real</p>
              <h2 id="agent-production-title">Validação em produção controlada</h2>
            </div>
            <span className="agents-draft-chip">Rastros no atendimento</span>
          </div>
          <p className="agents-panel-intro">Os testes da Alyce acontecem na conversa real. Cada mensagem, dado coletado, confirmação e encaminhamento fica registrado no atendimento para revisão.</p>
          <div className="agents-tools-grid agents-production-grid">
            <article className="agents-tool-card"><span className="agents-tool-status is-success">Canal ativo</span><h3>WhatsApp via WAHA</h3><p>O cliente conversa com a Alyce pelo número conectado da Evoluir.</p></article>
            <article className="agents-tool-card"><span className="agents-tool-status is-primary">Roteiro obrigatório</span><h3>Coleta progressiva</h3><p>Plano, endereço, titular, contato, e-mail e CPF/CNPJ são validados antes do encaminhamento.</p></article>
            <article className="agents-tool-card"><span className="agents-tool-status is-warning">Limite seguro</span><h3>Cobertura a confirmar</h3><p>Sem GeoGrid, a Alyce não confirma CTO, cobertura, prazo ou instalação gratuita.</p></article>
            <article className="agents-tool-card"><span className="agents-tool-status is-success">Auditoria</span><h3>Fila comercial</h3><p>Ao concluir, a solicitação abre na plataforma com o contexto do atendimento.</p></article>
          </div>
        </section>
      );
    }

    if (activeSection === 'versions') {
      return (
        <section className="agents-content-panel">
          <AgentEmptyState
            title="Controle de versões ainda não criado"
            description="Cada alteração futura ficará registrada, com prévia, autor, data e opção de retorno seguro para uma versão anterior."
          />
        </section>
      );
    }

    return (
      <section className="agents-overview-grid" aria-label="Resumo do agente">
        <article className="agents-hero-card">
          <div className="agents-hero-topline">
            <span className="agents-agent-avatar">AR</span>
            <div>
              <p className="agents-eyebrow">Agente de vendas</p>
              <h2>Alyce Rebeca</h2>
              <p className="agents-company-name">Evoluir Telecom</p>
            </div>
            <span className="agents-draft-chip">Contrato comercial</span>
          </div>
          <p className="agents-hero-description">Conduz o cliente desde o primeiro contato até a proposta de instalação, preservando confirmação humana para operações e informações ainda não integradas.</p>
          <div className="agents-policy-grid">
            <div><span>Catálogo inicial</span><strong>100 · 300 · 600 Mega</strong></div>
            <div><span>Instalação padrão</span><strong>R$ 150,00</strong></div>
            <div><span>Cobertura</span><strong>A confirmar via GeoGrid</strong></div>
          </div>
        </article>

        <aside className="agents-guardrail-card">
          <p className="agents-eyebrow">Regra inegociável</p>
          <h2>Sem promessa sem fonte</h2>
          <p>O agente não confirma cobertura, CTO disponível, instalação gratuita ou prazo enquanto a fonte correspondente não estiver consultável.</p>
          <div className="agents-guardrail-rule">
            <span aria-hidden="true">!</span>
            <p>GeoGrid é o bloqueio atual antes de ofertar disponibilidade.</p>
          </div>
        </aside>

        <article className="agents-summary-card">
          <div className="agents-panel-heading">
            <div>
              <p className="agents-eyebrow">Dados da proposta</p>
              <h2>Ficha de instalação</h2>
            </div>
            <span className="agents-count-chip">10 campos</span>
          </div>
          <p>A estrutura já está definida para que os dados coletados virem uma proposta auditável antes do envio ao SGP.</p>
          <div className="agents-field-chips" aria-label="Campos previstos">
            {['Plano', 'Nome completo', 'Telefone', 'CPF ou CNPJ', 'E-mail', 'Cidade', 'Bairro', 'Rua', 'Número', 'Complemento'].map((field) => (
              <span key={field}>{field}</span>
            ))}
          </div>
        </article>
      </section>
    );
  };

  return (
    <div className="page agents-view">
      <Topbar title="Agentes" subtitle="Desenvolva agentes comerciais com etapas, fontes e limites seguros." />

      <section className="agents-commandbar" aria-label="Contexto do agente">
        <div>
          <p className="agents-eyebrow">Estúdio de agentes</p>
          <h1>Agente comercial</h1>
        </div>
        <div className="agents-command-controls">
          <label>
            <span>Empresa</span>
            <select value={companyId} onChange={(event) => setCompanyId(event.target.value)} disabled={companies.length === 0}>
              {companies.length === 0 && <option>Carregando empresas...</option>}
              {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
            </select>
          </label>
          <div className="agents-command-status">
            <span className="agents-status-dot" aria-hidden="true" />
            <span>{selectedCompany ? 'Configuração isolada por empresa' : 'Nenhuma empresa carregada'}</span>
          </div>
          <button className="btn btn-secondary" type="button" disabled title="A criação será liberada após a estrutura de versões ser implementada">
            Novo agente
          </button>
        </div>
      </section>

      <div className="agents-studio-shell">
        <aside className="agents-catalog" aria-label="Catálogo de agentes">
          <p className="agents-catalog-label">Catálogo</p>
          <button type="button" className="agents-catalog-item is-active" aria-current="page">
            <span className="agents-catalog-initials">AR</span>
            <span>
              <strong>Alyce Rebeca</strong>
              <small>SDR · Evoluir Telecom</small>
            </span>
            <i>Produção</i>
          </button>
          <div className="agents-catalog-note">
            <strong>Produção controlada</strong>
            <p>O WhatsApp real é o ambiente de validação. As mudanças devem ser publicadas antes de entrarem no atendimento.</p>
          </div>
        </aside>

        <section className="agents-detail-area">
          <nav className="agents-tabs" aria-label="Seções do agente">
            {agentSections.map((section) => (
              <button
                className={activeSection === section.id ? 'is-active' : ''}
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
              >
                {section.label}
              </button>
            ))}
          </nav>
          {renderPanel()}
        </section>
      </div>
    </div>
  );
}
